"use client"

import React from "react"
import { useState, createContext, useContext, useMemo, useEffect } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { ChevronDown, Menu, X } from "lucide-react"
import { Link, type LinkProps } from "react-router-dom"
import { cn } from "../../lib/utils"
import type { Links } from "../../config/sidebar-links"
import userService from "../../services/users/userService"
import storeService from "../../services/cash-register/storeService"
import colorService from "../../services/colorService"
import { Crown } from "lucide-react"
// Constants for localStorage keys
const LOCAL_STORAGE_PRIMARY_COLOR = "app_primary_color";
const LOCAL_STORAGE_SECONDARY_COLOR = "app_secondary_color";

// Default colors
const DEFAULT_PRIMARY_COLOR = "#10b981";
const DEFAULT_SECONDARY_COLOR = "#ffffff";

interface SidebarContextProps {
    open: boolean
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    animate: boolean
    themeColors?: {
        primaryColor: string
        secondaryColor: string
    }
}

const SidebarContext = createContext<SidebarContextProps | undefined>(undefined)

export const useSidebar = () => {
    const context = useContext(SidebarContext)
    if (!context) {
        throw new Error("useSidebar must be used within a SidebarProvider")
    }
    return context
}

// Helper function to get colors from localStorage
const getColorsFromLocalStorage = () => {
    let primaryColor = DEFAULT_PRIMARY_COLOR;
    let secondaryColor = DEFAULT_SECONDARY_COLOR;

    try {
        // Try to get colors from localStorage
        const storedPrimaryColor = localStorage.getItem(LOCAL_STORAGE_PRIMARY_COLOR);
        const storedSecondaryColor = localStorage.getItem(LOCAL_STORAGE_SECONDARY_COLOR);

        if (storedPrimaryColor) {
            primaryColor = storedPrimaryColor;
        }

        if (storedSecondaryColor) {
            secondaryColor = storedSecondaryColor;
        }
    } catch (error) {
        console.error("Failed to access localStorage:", error);
    }

    return { primaryColor, secondaryColor };
};

// Helper function to save colors to localStorage
const saveColorsToLocalStorage = (primaryColor: string, secondaryColor: string) => {
    try {
        localStorage.setItem(LOCAL_STORAGE_PRIMARY_COLOR, primaryColor);
        localStorage.setItem(LOCAL_STORAGE_SECONDARY_COLOR, secondaryColor);
    } catch (error) {
        console.error("Failed to save to localStorage:", error);
    }
};

export const SidebarProvider = ({
    children,
    open: openProp,
    setOpen: setOpenProp,
    animate = true,
}: {
    children: React.ReactNode
    open?: boolean
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>
    animate?: boolean
}) => {
    const [openState, setOpenState] = useState(false)

    // Initialize colors from localStorage immediately
    const [themeColors, setThemeColors] = useState(getColorsFromLocalStorage())

    const open = openProp !== undefined ? openProp : openState
    const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState

    // Function to apply colors using colorService
    const applyColors = (primaryColor: string, secondaryColor: string) => {
        // Update theme state
        setThemeColors({ primaryColor, secondaryColor });

        // Apply colors globally through colorService
        colorService.setColors({
            primary_color: primaryColor,
            secondary_color: secondaryColor,
        });

        // Save to localStorage for future use
        saveColorsToLocalStorage(primaryColor, secondaryColor);

        // Apply directly to CSS variables for immediate effect
        document.documentElement.style.setProperty('--primary-color', primaryColor);
        document.documentElement.style.setProperty('--secondary-color', secondaryColor);
    };

    // Apply the initial colors from localStorage immediately
    useEffect(() => {
        applyColors(themeColors.primaryColor, themeColors.secondaryColor);
    }, []);

    // Load theme colors from store (backend)
    useEffect(() => {
        const loadColorsFromBackend = async () => {
            try {
                const storeData = await storeService.getStoreData();

                if (storeData.primary_color && storeData.secondary_color) {
                    // Backend colors are available, update everything
                    applyColors(storeData.primary_color, storeData.secondary_color);
                    console.log("Colors loaded from backend and applied");
                }
            } catch (error) {
                console.error("Failed to load theme colors from backend:", error);
                // Still using localStorage colors as fallback
            }
        };

        // Load from backend after using localStorage colors
        loadColorsFromBackend();
    }, []);

    return <SidebarContext.Provider value={{ open, setOpen, animate, themeColors }}>{children}</SidebarContext.Provider>
}

export const Sidebar = ({
    children,
    open,
    setOpen,
    animate,
}: {
    children: React.ReactNode
    open?: boolean
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>
    animate?: boolean
}) => {
    return (
        <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
            {children}
        </SidebarProvider>
    )
}

export const SidebarBody = (props: React.ComponentProps<typeof motion.div>) => {
    return (
        <>
            <DesktopSidebar {...props} />
            <MobileSidebar {...(props as React.ComponentProps<"div">)} />
        </>
    )
}

export const DesktopSidebar = ({ className, children, ...props }: React.ComponentProps<typeof motion.div>) => {
    const { open, setOpen, animate } = useSidebar()
    return (
        <motion.div
            className={cn(
                "px-4 py-4 hidden md:flex md:flex-col bg-neutral-100 dark:bg-neutral-800 w-[300px] flex-shrink-0",
                className,
            )}
            animate={{
                width: animate ? (open ? "300px" : "60px") : "300px",
            }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            {...props}
        >
            {children}
        </motion.div>
    )
}

export const MobileSidebar = ({ className, children, ...props }: React.ComponentProps<"div">) => {
    const { open, setOpen } = useSidebar()
    return (
        <>
            <div
                className={cn(
                    "h-10 px-4 py-4 flex flex-row md:hidden items-center justify-between bg-neutral-100 dark:bg-neutral-800 w-full",
                )}
                {...props}
            >
                <div className="flex justify-end z-20 w-full">
                    <Menu className="text-neutral-800 dark:text-neutral-200 cursor-pointer" onClick={() => setOpen(!open)} />
                </div>
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ x: "-100%", opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: "-100%", opacity: 0 }}
                            transition={{
                                duration: 0.3,
                                ease: "easeInOut",
                            }}
                            className={cn(
                                "fixed h-full w-full inset-0 bg-white dark:bg-neutral-900 p-10 z-[100] flex flex-col justify-between",
                                className,
                            )}
                        >
                            <div
                                className="absolute right-10 top-10 z-50 text-neutral-800 dark:text-neutral-200 cursor-pointer"
                                onClick={() => setOpen(!open)}
                            >
                                <X />
                            </div>
                            {children}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    )
}

export const SidebarLink = ({
    link,
    className,
    ...props
}: {
    link: Links
    className?: string
    props?: LinkProps
}) => {
    const { open, animate } = useSidebar()
    const [isHovered, setIsHovered] = useState(false)

    // Get current user role
    const currentRole = userService.getRoleFromToken()

    // Check if a link/sublink is allowed for the current role based on allowedRoles
    const isAllowedForRole = (allowedRoles?: string[]) => {
        if (!allowedRoles || allowedRoles.length === 0) return true;
        return allowedRoles.includes(currentRole || "");
    };

    // Filter sublinks based on the allowedRoles property
    const filteredSublinks = useMemo(() => {
        if (!link.sublinks) return [];
        return link.sublinks.filter(sublink => isAllowedForRole(sublink.allowedRoles));
    }, [link.sublinks, currentRole]);

    // Determine if the link should be visible based on allowedRoles
    const isLinkVisible = useMemo(() => {
        // Check if the link itself is allowed for the current role
        if (!isAllowedForRole(link.allowedRoles)) return false;

        // If it's a section with sublinks, show only if at least one sublink is visible
        if (link.isSection && link.sublinks) {
            return filteredSublinks.length > 0;
        }

        // For regular links (non-sections)
        return true;
    }, [link, currentRole, filteredSublinks]);

    // If the link is not visible based on role permissions, return null
    if (!isLinkVisible) {
        return null;
    }

    const content = (
        <>
            {link.icon}
            <motion.span
                animate={{
                    display: animate ? (open ? "inline-block" : "none") : "inline-block",
                    opacity: animate ? (open ? 1 : 0) : 1,
                }}
                className="text-neutral-700 dark:text-neutral-200 text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0 flex-grow"
            >
                {link.label}

                {/* Add PRO icon/badge if the link is a pro feature */}
                {link.isPro && open && (
                    <span className="ml-2 inline-flex items-center">
                        <Crown className="h-4 w-4 text-yellow-500" />
                        <span className="ml-1 text-xs font-semibold text-yellow-500">PRO</span>
                    </span>
                )}
            </motion.span>

            {filteredSublinks.length > 0 && open && (
                <ChevronDown className={`ml-auto h-4 w-4 transition-transform duration-200 ${isHovered ? "rotate-180" : ""}`} />
            )}
        </>
    )

    // Add special styling for Pro features or non-clickable items
    const containerClass = cn(
        "flex items-center justify-start gap-2 group/sidebar py-2",
        {
            "opacity-70 cursor-not-allowed": !link.isClickable || link.isPro, // Dimmed and shows not-allowed cursor
            "hover:bg-neutral-200 dark:hover:bg-neutral-700 cursor-pointer": link.isClickable && !link.isPro, // Only add hover effect if clickable
        },
        className
    )

    return (
        <div className="relative" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            {/* Choose whether to render a clickable link or just a div */}
            {(link.isSection || !link.isClickable || link.isPro) ? (
                <div className={containerClass} onClick={() => {
                    // If it's a pro feature, show a tooltip or notification
                    if (link.isPro) {
                        // You can add a toast or modal here
                        alert("This is a PRO feature. Please upgrade to access E-Boutique features.");
                    }
                }}>
                    {content}
                </div>
            ) : (
                <Link to={link.href!} className={containerClass} {...props}>
                    {content}
                </Link>
            )}

            {/* Show sublinks for sections, but mark them as pro if needed */}
            {filteredSublinks.length > 0 && (
                <AnimatePresence>
                    {isHovered && open && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden ml-12"
                        >
                            {filteredSublinks.map((sublink, idx) => (
                                <div
                                    key={idx}
                                    onClick={(e) => {
                                        // Prevent navigation for pro features
                                        if (link.isPro) {
                                            e.preventDefault();
                                            alert("This is a PRO feature. Please upgrade to access E-Boutique features.");
                                        }
                                    }}
                                    className={`flex items-center py-2 text-sm ${link.isPro
                                            ? "text-neutral-500 dark:text-neutral-500 cursor-not-allowed opacity-70"
                                            : "text-neutral-600 dark:text-neutral-300 hover:translate-x-2 transition-all duration-200 cursor-pointer"
                                        }`}
                                >
                                    {/* Only wrap with Link if not a Pro feature */}
                                    {link.isPro ? (
                                        <>
                                            {sublink.label}
                                            <span className="ml-2 text-xs font-semibold text-yellow-500">PRO</span>
                                        </>
                                    ) : (
                                        <Link to={sublink.href} className="w-full">
                                            {sublink.label}
                                        </Link>
                                    )}
                                </div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </div>
    )
}